'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Loader2, Globe, Users, FileText, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useCommunities } from '../../hooks/use-communities';
import { useAuthStore } from '../../stores';
import { toast } from 'sonner';
import { generateSlug } from '../../utils/slug';

const COMMUNITY_CATEGORIES = [
  'Tech',
  'Dev Tools',
  'Gaming',
  'AI',
  'Support',
  'Media',
  'Open Source',
  'Hardware',
  'Finance',
  'Education',
  'Lifestyle',
  'Other',
];

export default function AddCommunityModal({ open, onOpenChange, onSuccess }) {
  const { walletAddress } = useAuthStore();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    rules: '',
    website: '',
    isPrivate: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { useCreateCommunity } = useCommunities();
  const createCommunityMutation = useCreateCommunity();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCategoryChange = (value) => {
    setFormData((prev) => ({ ...prev, category: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if wallet is connected
    if (!walletAddress) {
      toast.error('Please connect your wallet first');
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      toast.error('Community name is required');
      return;
    }
    if (formData.name.length < 3) {
      toast.error('Community name must be at least 3 characters');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    setIsSubmitting(true);

    try {
      // Generate slug from name
      const slug = generateSlug(formData.name.trim());

      const result = await createCommunityMutation.mutateAsync({
        name: formData.name.trim(),
        slug,
        description: formData.description.trim(),
        category: formData.category,
        rules: formData.rules.trim() || null,
        website: formData.website.trim() || null,
        isPrivate: formData.isPrivate,
        creator: walletAddress, // Add creator wallet address
      });

      toast.success('Community created successfully!');

      // Reset form
      setFormData({
        name: '',
        description: '',
        category: '',
        rules: '',
        website: '',
        isPrivate: false,
      });

      onSuccess?.(result);
      onOpenChange?.(false);
    } catch (error) {
      console.error('Error creating community:', error);
      toast.error(
        error.response?.data?.message || 'Failed to create community'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onOpenChange?.(false)}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-900/95 backdrop-blur-xl shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-white/10 bg-zinc-900/95 backdrop-blur-xl">
            <div>
              <h2 className="text-xl font-bold text-white">Create Community</h2>
              <p className="text-sm text-zinc-400 mt-1">
                Build your own space on Talktrail
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange?.(false)}
              className="rounded-full hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Community Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Users className="w-4 h-4 text-violet-400" />
                Community Name *
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Web3 Developers"
                className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500"
                maxLength={50}
              />
              <p className="text-xs text-zinc-500">
                {formData.name.length}/50 characters
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <FileText className="w-4 h-4 text-violet-400" />
                Description *
              </label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="What is your community about?"
                className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500 min-h-[100px]"
                maxLength={500}
              />
              <p className="text-xs text-zinc-500">
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Tag className="w-4 h-4 text-violet-400" />
                Category *
              </label>
              <Select
                value={formData.category}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {COMMUNITY_CATEGORIES.map((cat) => (
                    <SelectItem
                      key={cat}
                      value={cat}
                      className="text-white hover:bg-white/10"
                    >
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Website (Optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Globe className="w-4 h-4 text-violet-400" />
                Website (optional)
              </label>
              <Input
                name="website"
                type="url"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://your-community.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500"
              />
            </div>

            {/* Rules (Optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">
                Community Rules (optional)
              </label>
              <Textarea
                name="rules"
                value={formData.rules}
                onChange={handleInputChange}
                placeholder="Set guidelines for your community members..."
                className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500 min-h-[80px]"
                maxLength={1000}
              />
            </div>

            {/* Private Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
              <div>
                <p className="text-sm font-medium text-white">
                  Private Community
                </p>
                <p className="text-xs text-zinc-400">
                  Only approved members can see and post
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="isPrivate"
                  checked={formData.isPrivate}
                  onChange={handleInputChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                className="flex-1 border-white/10 text-zinc-300 hover:bg-white/5"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Community'
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
